/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 *
 * The shopping model, as plain data and pure functions.
 *
 * Two layers, and the screen only ever sees the second:
 *
 *   confirmed — the server's state: the snapshot plus every change from the
 *               household feed, in order. Nothing local ever writes here.
 *   view      — confirmed with this device's not-yet-confirmed operations
 *               replayed on top, so a tap shows instantly and survives a
 *               reconnect: when the server's change arrives, the local
 *               replay is simply dropped and the two agree.
 *
 * Kept free of React and of the network so it can be tested as data.
 */

import type {
  LiveCategory, LiveChange, LiveClaim, LiveItem, LiveList, LiveMember, LiveSession, LiveShare,
  LiveState, LiveStore, ShoppingOp,
} from "../../../api/shoppingLive.ts";

export interface Model {
  lists: Record<string, LiveList>;
  items: Record<string, LiveItem>;
  /** Deleted items, so "undo" can show the restore before the server confirms. */
  graveyard: Record<string, LiveItem>;
  categories: LiveCategory[];
  members: LiveMember[];
  stores: LiveStore[];
  claims: LiveClaim[];
  sessions: LiveSession[];
  shares: LiveShare[];
  me: string;
  seq: number;
}

export function fromState(s: LiveState): Model {
  const lists: Record<string, LiveList> = {};
  for (const l of s.lists) lists[l.id] = l;
  const items: Record<string, LiveItem> = {};
  for (const i of s.items) items[i.id] = normalizeItem(i);
  return {
    lists, items, graveyard: {}, categories: sortCategories(s.categories), members: s.members,
    stores: sortStores(s.stores ?? []), claims: s.claims ?? [], sessions: s.sessions ?? [],
    shares: s.shares ?? [], me: s.me, seq: s.latestSeq,
  };
}

const sortCategories = (c: LiveCategory[]) =>
  [...c].sort((a, b) => a.sort_order - b.sort_order || a.name.localeCompare(b.name));
const sortStores = (s: LiveStore[]) =>
  [...s].sort((a, b) => Number(a.sort_order) - Number(b.sort_order) || a.name.localeCompare(b.name));

function normalizeItem(i: LiveItem): LiveItem {
  return {
    ...i,
    quantity: i.quantity === null || i.quantity === undefined ? null : Number(i.quantity),
    position: i.position === null || i.position === undefined ? null : Number(i.position),
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// The feed
// ─────────────────────────────────────────────────────────────────────────────

export function applyChange(m: Model, c: LiveChange): Model {
  const seq = Math.max(m.seq, c.seq);
  switch (c.op) {
    case "item.upsert": {
      const item = normalizeItem(c.payload as unknown as LiveItem);
      if (!item.id) return { ...m, seq };
      const graveyard = { ...m.graveyard };
      delete graveyard[item.id];
      return { ...m, seq, items: { ...m.items, [item.id]: item }, graveyard };
    }
    case "item.delete":
    case "item.purge": {
      const id = c.itemId ?? (c.payload.id as string | undefined);
      if (!id) return { ...m, seq };
      const items = { ...m.items };
      const gone = items[id];
      delete items[id];
      const graveyard = { ...m.graveyard };
      if (c.op === "item.delete") {
        graveyard[id] = normalizeItem({ ...(gone ?? {}), ...(c.payload as unknown as LiveItem) });
      } else {
        delete graveyard[id];
      }
      return { ...m, seq, items, graveyard };
    }
    case "list.upsert": {
      const list = c.payload as unknown as LiveList;
      if (!list.id) return { ...m, seq };
      return { ...m, seq, lists: { ...m.lists, [list.id]: { ...m.lists[list.id], ...list } } };
    }
    case "list.delete": {
      const id = c.listId;
      if (!id) return { ...m, seq };
      const lists = { ...m.lists };
      delete lists[id];
      const items: Record<string, LiveItem> = {};
      for (const [k, v] of Object.entries(m.items)) if (v.list_id !== id) items[k] = v;
      return { ...m, seq, lists, items };
    }
    case "store.upsert": {
      const st = c.payload as unknown as LiveStore;
      if (!st.id) return { ...m, seq };
      return { ...m, seq, stores: sortStores([...m.stores.filter((x) => x.id !== st.id), st]) };
    }
    case "store.delete": {
      const id = (c.payload as { id?: string }).id;
      return { ...m, seq, stores: m.stores.filter((x) => x.id !== id) };
    }
    case "category.upsert": {
      const cat = c.payload as unknown as LiveCategory;
      if (!cat.key) return { ...m, seq };
      return { ...m, seq, categories: sortCategories([...m.categories.filter((x) => x.key !== cat.key), cat]) };
    }
    case "category.delete": {
      const key = (c.payload as { key?: string }).key;
      return { ...m, seq, categories: m.categories.filter((x) => x.key !== key) };
    }
    case "claim.upsert": {
      const cl = c.payload as unknown as LiveClaim;
      return {
        ...m, seq,
        claims: [...m.claims.filter((x) => !(x.list_id === cl.list_id && x.category_key === cl.category_key)), cl],
      };
    }
    case "claim.delete": {
      const cl = c.payload as unknown as LiveClaim;
      return {
        ...m, seq,
        claims: m.claims.filter((x) => !(x.list_id === cl.list_id && x.category_key === cl.category_key)),
      };
    }
    case "session.upsert": {
      const se = c.payload as unknown as LiveSession;
      const rest = m.sessions.filter((x) => x.id !== se.id);
      return { ...m, seq, sessions: se.ended_at ? rest : [...rest, se] };
    }
    case "share.upsert": {
      const sh = c.payload as unknown as LiveShare;
      const rest = m.shares.filter((x) => x.id !== sh.id);
      const live = !sh.revoked_at && new Date(sh.expires_at).getTime() > Date.now();
      return { ...m, seq, shares: live ? [sh, ...rest] : rest };
    }
    case "share.delete": {
      const id = (c.payload as { id?: string }).id;
      return { ...m, seq, shares: m.shares.filter((x) => x.id !== id) };
    }
    default:
      return { ...m, seq };
  }
}

/** The server's key for a new category name (it adds "-2" on a clash). */
export function categorySlug(name: string): string {
  return name.toLowerCase().normalize("NFKD").replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 40) || "category";
}

// ─────────────────────────────────────────────────────────────────────────────
// Local operations, shown before the server confirms them
// ─────────────────────────────────────────────────────────────────────────────

export function homeListId(m: Model): string | null {
  return Object.values(m.lists).find((l) => l.kind === "home")?.id ?? null;
}

const norm = (s: string) => s.trim().toLowerCase().replace(/\s+/g, " ");

/** The store the family last chose for this, from what's already on a list. */
export function guessStore(m: Model, name: string): string | null {
  const n = norm(name);
  const hit = [...Object.values(m.items), ...Object.values(m.graveyard)]
    .filter((i) => norm(i.display_name) === n && i.store_id)
    .sort((a, b) => b.updated_at.localeCompare(a.updated_at))[0];
  return hit?.store_id ?? null;
}

/** What a family usually files this under, from anything already on a list. */
export function guessAisle(m: Model, name: string): string | null {
  const n = norm(name);
  for (const i of [...Object.values(m.items), ...Object.values(m.graveyard)]) {
    if (norm(i.display_name) === n && i.aisle) return i.aisle;
  }
  return null;
}

const FIELD_ALIASES: Record<string, keyof LiveItem> = {
  name: "display_name", displayName: "display_name", display_name: "display_name",
  category: "aisle", aisle: "aisle", quantity: "quantity", unit: "unit",
  notes: "notes", brand: "brand", urgent: "urgent",
  storeId: "store_id", store: "store_id", store_id: "store_id",
};

const BUILT_IN = new Set(["produce", "bakery", "meat", "fish", "dairy", "chilled", "pantry", "baking",
  "drinks", "frozen", "household", "personal-care", "baby", "pharmacy", "pets", "other"]);

export function applyOp(m: Model, op: ShoppingOp, nowIso: string): Model {
  const s = (v: unknown) => (typeof v === "string" ? v : null);
  switch (op.op) {
    case "list.create": {
      const id = s(op.listId);
      if (!id) return m;
      const list: LiveList = {
        id, title: s(op.title) ?? "New list", kind: "custom", icon: s(op.icon), color: s(op.color),
        pinned: false, status: "active", store_id: null, archived_at: null,
        created_by: m.me, updated_by: m.me, created_at: nowIso, updated_at: nowIso,
        is_stale: false, covers_from: null, covers_to: null,
      };
      return { ...m, lists: { ...m.lists, [id]: list } };
    }
    case "list.update": {
      const l = m.lists[s(op.listId) ?? ""];
      if (!l) return m;
      const next = { ...l, updated_at: nowIso, updated_by: m.me };
      if (op.title !== undefined) next.title = s(op.title) ?? l.title;
      if (op.icon !== undefined) next.icon = s(op.icon);
      if (op.color !== undefined) next.color = s(op.color);
      if (op.pinned !== undefined) next.pinned = op.pinned === true;
      return { ...m, lists: { ...m.lists, [l.id]: next } };
    }
    case "list.archive":
    case "list.restore": {
      const l = m.lists[s(op.listId) ?? ""];
      if (!l || (op.op === "list.archive" && l.kind === "home")) return m;
      return {
        ...m,
        lists: {
          ...m.lists,
          [l.id]: { ...l, archived_at: op.op === "list.archive" ? nowIso : null, updated_at: nowIso },
        },
      };
    }
    case "list.delete": {
      const id = s(op.listId) ?? "";
      if (!m.lists[id] || m.lists[id]!.kind === "home") return m;
      const lists = { ...m.lists };
      delete lists[id];
      const items: Record<string, LiveItem> = {};
      for (const [k, v] of Object.entries(m.items)) if (v.list_id !== id) items[k] = v;
      return { ...m, lists, items };
    }
    case "list.duplicate": {
      const from = m.lists[s(op.listId) ?? ""];
      const id = s(op.newListId);
      if (!from || !id) return m;
      const list: LiveList = {
        ...from, id, kind: "custom", pinned: false, archived_at: null,
        title: s(op.title) ?? `${from.title} (copy)`, created_at: nowIso, updated_at: nowIso,
        created_by: m.me, updated_by: m.me, is_stale: false,
      };
      return { ...m, lists: { ...m.lists, [id]: list } };
    }
    case "list.clearGot": {
      const id = s(op.listId);
      const items: Record<string, LiveItem> = {};
      const graveyard = { ...m.graveyard };
      for (const [k, v] of Object.entries(m.items)) {
        if (v.list_id === id && v.status === "got") graveyard[k] = v;
        else items[k] = v;
      }
      return { ...m, items, graveyard };
    }

    case "item.add": {
      const listId = !op.listId || op.listId === "home" ? homeListId(m) : s(op.listId);
      const id = s(op.itemId);
      if (!listId || !id) return m;
      const name = (s(op.name) ?? s(op.text) ?? "").trim();
      if (!name) return m;
      const quantity = typeof op.quantity === "number" ? op.quantity : null;
      const unit = s(op.unit);
      // Mirror the server's merge for the common case, so a repeat doesn't
      // flash a second row before the server folds it into the first.
      if (s(op.name)) {
        const same = Object.values(m.items).find((i) =>
          i.list_id === listId && i.status === "needed" && norm(i.display_name) === norm(name)
          && (i.unit ?? null) === (unit ?? null));
        if (same) {
          const q = same.quantity === null && quantity === null ? null
            : (same.quantity ?? 1) + (quantity ?? 1);
          return { ...m, items: { ...m.items, [same.id]: { ...same, quantity: q, updated_at: nowIso } } };
        }
      }
      const position = Math.max(0, ...Object.values(m.items)
        .filter((i) => i.list_id === listId).map((i) => i.position ?? 0)) + 1;
      const item: LiveItem = {
        id, list_id: listId, food_id: null, display_name: name, quantity, unit,
        quantity_note: null, aisle: s(op.aisle) ?? guessAisle(m, name) ?? "other",
        origin: "manual", status: "needed", is_checked: false, checked_by: null, checked_at: null,
        notes: s(op.notes), brand: s(op.brand), urgent: op.urgent === true, position,
        added_by: m.me, updated_by: m.me, created_at: nowIso, updated_at: nowIso,
        store_id: op.storeId !== undefined ? s(op.storeId) : guessStore(m, name),
      };
      return { ...m, items: { ...m.items, [id]: item } };
    }
    case "item.update": {
      const i = m.items[s(op.itemId) ?? ""];
      if (!i) return m;
      const next: LiveItem = { ...i, updated_at: nowIso, updated_by: m.me };
      for (const [k, v] of Object.entries((op.fields ?? {}) as Record<string, unknown>)) {
        const f = FIELD_ALIASES[k];
        if (!f) continue;
        if (f === "quantity") next.quantity = typeof v === "number" && v > 0 ? v : null;
        else if (f === "urgent") next.urgent = v === true;
        else if (f === "display_name") next.display_name = s(v)?.trim() || i.display_name;
        else if (f === "aisle") next.aisle = s(v) || "other";
        else if (f === "store_id") next.store_id = s(v);
        else (next as unknown as Record<string, unknown>)[f] = s(v);
      }
      return { ...m, items: { ...m.items, [i.id]: next } };
    }
    case "item.status": {
      const i = m.items[s(op.itemId) ?? ""];
      const status = op.status;
      if (!i || (status !== "needed" && status !== "got" && status !== "unavailable")) return m;
      return {
        ...m,
        items: {
          ...m.items,
          [i.id]: {
            ...i, status, is_checked: status === "got", updated_at: nowIso,
            checked_by: status === "got" ? m.me : i.checked_by,
          },
        },
      };
    }
    case "item.move":
    case "item.copy": {
      const i = m.items[s(op.itemId) ?? ""];
      const to = s(op.toListId);
      const newId = s(op.newItemId);
      if (!i || !to || !newId || !m.lists[to]) return m;
      const items = { ...m.items };
      const graveyard = { ...m.graveyard };
      items[newId] = {
        ...i, id: newId, list_id: to, status: i.status === "got" ? "needed" : i.status,
        is_checked: false, updated_at: nowIso,
      };
      if (op.op === "item.move" && i.list_id !== to) { graveyard[i.id] = i; delete items[i.id]; }
      return { ...m, items, graveyard };
    }
    case "item.reorder": {
      const i = m.items[s(op.itemId) ?? ""];
      if (!i || typeof op.position !== "number") return m;
      return { ...m, items: { ...m.items, [i.id]: { ...i, position: op.position } } };
    }
    case "item.delete": {
      const i = m.items[s(op.itemId) ?? ""];
      if (!i) return m;
      const items = { ...m.items };
      delete items[i.id];
      return { ...m, items, graveyard: { ...m.graveyard, [i.id]: i } };
    }
    case "item.restore": {
      const id = s(op.itemId) ?? "";
      const i = m.graveyard[id];
      if (!i || m.items[id]) return m;
      const graveyard = { ...m.graveyard };
      delete graveyard[id];
      return { ...m, items: { ...m.items, [id]: { ...i, deleted_at: null } }, graveyard };
    }

    // ── templates ────────────────────────────────────────────────────────
    case "list.saveTemplate": {
      const from = m.lists[s(op.listId) ?? ""];
      const id = s(op.templateId);
      if (!from || !id) return m;
      return {
        ...m,
        lists: {
          ...m.lists,
          [id]: { ...from, id, kind: "template", pinned: false, archived_at: null,
            title: s(op.title) ?? from.title, created_at: nowIso, updated_at: nowIso, is_stale: false },
        },
      };
    }

    // ── stores ───────────────────────────────────────────────────────────
    case "store.create": {
      const id = s(op.storeId);
      const name = s(op.name)?.trim();
      if (!id || !name || m.stores.some((x) => x.name.toLowerCase() === name.toLowerCase())) return m;
      const sort = Math.max(0, ...m.stores.map((x) => Number(x.sort_order))) + 1;
      return { ...m, stores: sortStores([...m.stores,
        { id, name, icon: s(op.icon), sort_order: sort, is_default: false, aisle_order: [] }]) };
    }
    case "store.update": {
      const st = m.stores.find((x) => x.id === op.storeId);
      if (!st) return m;
      const next = { ...st };
      if (op.name !== undefined) next.name = s(op.name) ?? st.name;
      if (op.icon !== undefined) next.icon = s(op.icon);
      if (Array.isArray(op.aisleOrder)) next.aisle_order = op.aisleOrder.filter((k): k is string => typeof k === "string");
      let stores = m.stores.map((x) => (x.id === st.id ? next : x));
      if (op.isDefault === true) stores = stores.map((x) => ({ ...x, is_default: x.id === st.id }));
      return { ...m, stores };
    }
    case "store.reorder": {
      const ids = Array.isArray(op.storeIds) ? op.storeIds : [];
      return {
        ...m,
        stores: sortStores(m.stores.map((x) => {
          const i = ids.indexOf(x.id);
          return i === -1 ? x : { ...x, sort_order: i + 1 };
        })),
      };
    }
    case "store.delete": {
      const id = s(op.storeId);
      const items: Record<string, LiveItem> = {};
      for (const [k, v] of Object.entries(m.items)) items[k] = v.store_id === id ? { ...v, store_id: null } : v;
      return { ...m, items, stores: m.stores.filter((x) => x.id !== id) };
    }

    // ── categories ───────────────────────────────────────────────────────
    case "category.create": {
      const name = s(op.name)?.trim();
      if (!name) return m;
      const key = categorySlug(s(op.key) ?? name);
      if (m.categories.some((c) => c.key === key)) return m;
      const sort = Math.max(0, ...m.categories.filter((c) => c.key !== "other").map((c) => c.sort_order)) + 10;
      return { ...m, categories: sortCategories([...m.categories,
        { key, name, icon: s(op.icon), sort_order: sort, hidden: false }]) };
    }
    case "category.update": {
      const key = s(op.key);
      return {
        ...m,
        categories: m.categories.map((c) => c.key !== key ? c : {
          ...c,
          ...(op.name !== undefined ? { name: s(op.name) ?? c.name } : {}),
          ...(op.icon !== undefined ? { icon: s(op.icon) } : {}),
          ...(op.hidden !== undefined ? { hidden: op.hidden === true } : {}),
        }),
      };
    }
    case "category.reorder": {
      const keys = (Array.isArray(op.keys) ? op.keys : []).filter((k) => k !== "other");
      return {
        ...m,
        categories: sortCategories(m.categories.map((c) => {
          if (c.key === "other") return { ...c, sort_order: 999 };
          const i = keys.indexOf(c.key);
          return i === -1 ? c : { ...c, sort_order: (i + 1) * 10 };
        })),
      };
    }
    case "category.delete": {
      const key = s(op.key);
      if (!key || key === "other") return m;
      if (BUILT_IN.has(key)) {
        return { ...m, categories: m.categories.map((c) => (c.key === key ? { ...c, hidden: true } : c)) };
      }
      const items: Record<string, LiveItem> = {};
      for (const [k, v] of Object.entries(m.items)) items[k] = v.aisle === key ? { ...v, aisle: "other" } : v;
      return { ...m, items, categories: m.categories.filter((c) => c.key !== key) };
    }

    // ── shopping together ────────────────────────────────────────────────
    case "list.startShopping": {
      const listId = s(op.listId);
      if (!listId) return m;
      const session: LiveSession = {
        id: s(op.sessionId) ?? `local-${op.id}`, list_id: listId, member_sub: m.me,
        store_id: s(op.storeId), started_at: nowIso, ended_at: null,
      };
      return { ...m, sessions: [...m.sessions.filter((x) => x.member_sub !== m.me), session] };
    }
    case "list.stopShopping":
      return {
        ...m,
        sessions: m.sessions.filter((x) => !(x.member_sub === m.me && x.list_id === op.listId)),
        claims: m.claims.filter((x) => !(x.member_sub === m.me && x.list_id === op.listId)),
      };
    case "claim.set": {
      const listId = s(op.listId); const key = s(op.category);
      if (!listId || !key) return m;
      const held = m.claims.find((x) => x.list_id === listId && x.category_key === key);
      if (held && held.member_sub !== m.me && op.force !== true) return m;
      return {
        ...m,
        claims: [...m.claims.filter((x) => !(x.list_id === listId && x.category_key === key)),
          { list_id: listId, category_key: key, member_sub: m.me, claimed_at: nowIso }],
      };
    }
    case "claim.clear":
      return { ...m, claims: m.claims.filter((x) => !(x.list_id === op.listId && x.category_key === op.category)) };
    case "list.finishTrip": {
      const listId = s(op.listId);
      const items: Record<string, LiveItem> = {};
      const graveyard = { ...m.graveyard };
      for (const [k, v] of Object.entries(m.items)) {
        if (v.list_id !== listId) { items[k] = v; continue; }
        if (v.status === "got" && op.clearGot !== false) { graveyard[k] = v; continue; }
        if (v.status === "unavailable") {
          // To Home: the feed brings the Home copy; here it simply leaves.
          if (op.unavailable === "home" && homeListId(m) !== listId) { graveyard[k] = v; continue; }
          items[k] = { ...v, status: "needed", is_checked: false };
          continue;
        }
        items[k] = v;
      }
      return {
        ...m, items, graveyard,
        sessions: m.sessions.filter((x) => !(x.member_sub === m.me && x.list_id === listId)),
        claims: m.claims.filter((x) => !(x.member_sub === m.me && x.list_id === listId)),
      };
    }

    // ── links ────────────────────────────────────────────────────────────
    case "share.revoke":
      return { ...m, shares: m.shares.filter((x) => x.id !== op.shareId) };

    default:
      return m;
  }
}

/**
 * The operations that take `op` back, worked out from the view BEFORE `op`
 * is applied. Empty when there's nothing sensible to undo.
 */
export function inverseOf(m: Model, op: ShoppingOp, newId: () => string): Omit<ShoppingOp, "id">[] {
  const s = (v: unknown) => (typeof v === "string" ? v : null);
  switch (op.op) {
    case "item.add": {
      const listId = !op.listId || op.listId === "home" ? homeListId(m) : s(op.listId);
      const name = s(op.name);
      const same = name ? Object.values(m.items).find((i) =>
        i.list_id === listId && i.status === "needed" && norm(i.display_name) === norm(name)
        && (i.unit ?? null) === (s(op.unit) ?? null)) : undefined;
      if (same) {
        return [{ op: "item.update", itemId: same.id, fields: { quantity: same.quantity } }];
      }
      return [{ op: "item.delete", itemId: op.itemId }];
    }
    case "item.delete": return [{ op: "item.restore", itemId: op.itemId }];
    case "item.status": {
      const i = m.items[s(op.itemId) ?? ""];
      return i ? [{ op: "item.status", itemId: i.id, status: i.status }] : [];
    }
    case "item.update": {
      const i = m.items[s(op.itemId) ?? ""];
      if (!i) return [];
      const back: Record<string, unknown> = {};
      for (const k of Object.keys((op.fields ?? {}) as Record<string, unknown>)) {
        const f = FIELD_ALIASES[k];
        if (f === "store_id") back.storeId = i.store_id ?? null;
        else if (f) back[f === "display_name" ? "name" : f] = i[f];
      }
      return [{ op: "item.update", itemId: i.id, fields: back }];
    }
    case "item.move":
      return [{ op: "item.delete", itemId: op.newItemId }, { op: "item.restore", itemId: op.itemId }];
    case "item.copy":
      return [{ op: "item.delete", itemId: op.newItemId }];
    case "list.archive": return [{ op: "list.restore", listId: op.listId }];
    case "list.restore": return [{ op: "list.archive", listId: op.listId }];
    case "list.create": return [{ op: "list.archive", listId: op.listId }];
    case "list.update": {
      const l = m.lists[s(op.listId) ?? ""];
      if (!l) return [];
      const back: Record<string, unknown> = { op: "list.update", listId: l.id };
      for (const k of ["title", "icon", "color", "pinned"] as const) {
        if (op[k] !== undefined) back[k] = l[k];
      }
      return [back as Omit<ShoppingOp, "id">];
    }
    case "list.clearGot":
      return Object.values(m.items)
        .filter((i) => i.list_id === op.listId && i.status === "got")
        .map((i) => ({ op: "item.restore", itemId: i.id }));
    case "store.update": {
      const st = m.stores.find((x) => x.id === op.storeId);
      if (!st) return [];
      const back: Record<string, unknown> = { op: "store.update", storeId: st.id };
      if (op.name !== undefined) back.name = st.name;
      if (op.icon !== undefined) back.icon = st.icon;
      if (op.aisleOrder !== undefined) back.aisleOrder = st.aisle_order;
      return [back as Omit<ShoppingOp, "id">];
    }
    case "category.update": {
      const c = m.categories.find((x) => x.key === op.key);
      if (!c) return [];
      const back: Record<string, unknown> = { op: "category.update", key: c.key };
      if (op.name !== undefined) back.name = c.name;
      if (op.icon !== undefined) back.icon = c.icon;
      if (op.hidden !== undefined) back.hidden = !!c.hidden;
      return [back as Omit<ShoppingOp, "id">];
    }
    case "category.reorder":
      return [{ op: "category.reorder", keys: m.categories.map((c) => c.key) }];
    case "store.reorder":
      return [{ op: "store.reorder", storeIds: m.stores.map((x) => x.id) }];
    case "claim.set": {
      const held = m.claims.find((x) => x.list_id === op.listId && x.category_key === op.category);
      return held ? [] : [{ op: "claim.clear", listId: op.listId, category: op.category }];
    }
    default:
      void newId;
      return [];
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Reading the view
// ─────────────────────────────────────────────────────────────────────────────

export function liveLists(m: Model): LiveList[] {
  return Object.values(m.lists)
    .filter((l) => !l.archived_at && l.kind !== "template")
    .sort((a, b) =>
      Number(b.kind === "home") - Number(a.kind === "home")
      || Number(b.pinned) - Number(a.pinned)
      || b.updated_at.localeCompare(a.updated_at));
}

export function templateLists(m: Model): LiveList[] {
  return Object.values(m.lists).filter((l) => l.kind === "template")
    .sort((a, b) => a.title.localeCompare(b.title));
}

export function storeName(m: Model, id: string | null | undefined): string | null {
  return id ? m.stores.find((s) => s.id === id)?.name ?? null : null;
}

/** Category keys in the order to walk them: this store's own, else the family's. */
export function walkingOrder(m: Model, storeId: string | null): string[] {
  const family = m.categories.map((c) => c.key);
  const own = storeId ? m.stores.find((s) => s.id === storeId)?.aisle_order ?? [] : [];
  return [...own.filter((k) => family.includes(k)), ...family.filter((k) => !own.includes(k))];
}

export function sessionsOn(m: Model, listId: string): LiveSession[] {
  return m.sessions.filter((s) => s.list_id === listId && !s.ended_at);
}

export function claimOf(m: Model, listId: string, key: string): LiveClaim | undefined {
  return m.claims.find((c) => c.list_id === listId && c.category_key === key);
}

export function sharesOf(m: Model, listId: string): LiveShare[] {
  return m.shares.filter((s) => s.list_id === listId);
}

export function archivedLists(m: Model): LiveList[] {
  return Object.values(m.lists)
    .filter((l) => !!l.archived_at)
    .sort((a, b) => (b.archived_at ?? "").localeCompare(a.archived_at ?? ""));
}

export function itemsOf(m: Model, listId: string): LiveItem[] {
  return Object.values(m.items)
    .filter((i) => i.list_id === listId && !i.is_suppressed)
    .sort((a, b) => (a.position ?? 0) - (b.position ?? 0) || a.created_at.localeCompare(b.created_at));
}

export function counts(m: Model, listId: string): { needed: number; got: number } {
  let needed = 0; let got = 0;
  for (const i of Object.values(m.items)) {
    if (i.list_id !== listId || i.is_suppressed) continue;
    if (i.status === "got") got++; else needed++;
  }
  return { needed, got };
}

/**
 * Needed items grouped by category, in the household's category order (or a
 * store's walking order). `store`: undefined = every item; null = items with
 * no store; an id = that store's items plus the ones with no store.
 */
export function grouped(
  m: Model, listId: string, opts: { store?: string | null; order?: string[] } = {},
): Array<{ category: LiveCategory; items: LiveItem[] }> {
  const keys = opts.order ?? m.categories.map((c) => c.key);
  const order = new Map(keys.map((k, i) => [k, i]));
  const byKey = new Map<string, LiveItem[]>();
  for (const i of itemsOf(m, listId)) {
    if (i.status === "got") continue;
    if (opts.store === null && i.store_id) continue;
    if (opts.store && i.store_id && i.store_id !== opts.store) continue;
    const k = i.aisle && order.has(i.aisle) ? i.aisle : "other";
    byKey.set(k, [...(byKey.get(k) ?? []), i]);
  }
  return [...byKey.entries()]
    .map(([k, items]) => ({
      category: m.categories.find((c) => c.key === k)
        ?? { key: k, name: k, icon: null, sort_order: 999 },
      items: items.sort((a, b) => Number(b.urgent) - Number(a.urgent)
        || (a.position ?? 0) - (b.position ?? 0)),
    }))
    .sort((a, b) => (order.get(a.category.key) ?? 999) - (order.get(b.category.key) ?? 999));
}

/** "you", a family member's name, or "Croissant for Deborah". */
export function whoAdded(m: Model, i: LiveItem): string {
  if (i.added_by?.startsWith("share:")) {
    const label = m.shares.find((x) => `share:${x.id}` === i.added_by)?.label;
    return `${label ?? "a guest"}, via link`;
  }
  const person = i.added_by === m.me ? "you"
    : m.members.find((x) => x.sub === i.added_by)?.name ?? "family";
  if (i.origin === "chef") return person === "you" ? "Croissant, for you" : `Croissant, for ${person}`;
  if (i.origin === "generated") return "from the plan";
  return person;
}

export function memberName(m: Model, sub: string | null): string {
  if (!sub) return "someone";
  if (sub.startsWith("share:")) return m.shares.find((x) => `share:${x.id}` === sub)?.label ?? "a guest";
  if (sub === m.me) return "you";
  return m.members.find((x) => x.sub === sub)?.name ?? "someone";
}

export function describeQuantity(i: Pick<LiveItem, "quantity" | "unit">): string {
  if (i.quantity === null) return "";
  const q = Number.isInteger(i.quantity) ? String(i.quantity) : String(Math.round(i.quantity * 100) / 100);
  return i.unit && i.unit !== "ct" ? `${q} ${i.unit}` : `×${q}`;
}
